// PROTOTYPE ONLY (087 Super Match — ticket 10: prototype and approve Best Match
// experience). Approved visual treatment for the plan-wide "Best Match",
// hosted on the real Care Seeker / FP Q&A results pages via
// ?bestMatchPrototype=A (+ ?bestMatchScenario=...).
import type {
  NavigatorCategory,
  NavigatorProvider,
  NavigatorResultsViewModel,
} from '../navigator-results.types';

export type BestMatchVariantKey = 'A';

export const variantNames: Record<BestMatchVariantKey, string> = {
  A: 'Approved — highlight + provider label',
};

export type BestMatchScenario = 'live' | 'zero' | 'one' | 'loading' | 'error';

export const scenarios: { key: BestMatchScenario; label: string }[] = [
  { key: 'live', label: 'Live' },
  { key: 'zero', label: '0 matches' },
  { key: 'one', label: '1 match' },
  { key: 'loading', label: 'Loading' },
  { key: 'error', label: 'Error' },
];

export type BestMatchEntry = {
  provider: NavigatorProvider;
  category: NavigatorCategory;
  score: number;
  categoryOrder: number;
};

export type BestMatchResult = {
  status: 'loading' | 'error' | 'ready';
  entries: BestMatchEntry[];
  /** Provider ids in the global Best Match set — card/badge lookups key off this. */
  providerIds: Set<string>;
  /** Every scenario (besides a truly empty session) tops categories up with
   * fixture providers so the page previews populated, not sparse — see
   * `populateViewModel`. */
  viewModel: NavigatorResultsViewModel;
};

const hashString = (input: string): number => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) hash = (hash * 31 + input.charCodeAt(i)) | 0;
  return Math.abs(hash);
};

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

/**
 * Stand-in for the real `score` (ticket 05/09 add it to the API; the frontend
 * view-model doesn't carry it yet). Deterministic so re-renders are stable:
 * rank + rating dominate, a per-id hash breaks remaining ties.
 */
const mockScoreFor = (provider: NavigatorProvider): number => {
  const rankBonus = provider.rankOrder === 1 ? 0.22 : provider.rankOrder === 2 ? 0.08 : 0;
  const ratingBonus =
    typeof provider.rating === 'number' ? Math.max(0, provider.rating - 3.5) * 0.18 : 0;
  const jitter = (hashString(provider.id) % 15) / 100;
  return clamp01(0.62 + rankBonus + ratingBonus + jitter);
};

const compareBestMatchEntries = (a: BestMatchEntry, b: BestMatchEntry): number =>
  b.score - a.score ||
  Number(b.category.isPrimary) - Number(a.category.isPrimary) ||
  a.categoryOrder - b.categoryOrder ||
  a.provider.name.localeCompare(b.provider.name) ||
  a.provider.id.localeCompare(b.provider.id);

/** Pool every ranked row and keep each provider's strongest occurrence. */
const poolAcrossCategories = (viewModel: NavigatorResultsViewModel): BestMatchEntry[] => {
  const byProvider = new Map<string, BestMatchEntry>();
  viewModel.categories.forEach((category, categoryOrder) => {
    for (const provider of category.providers) {
      const score = mockScoreFor(provider);
      const candidate = { provider, category, score, categoryOrder };
      const existing = byProvider.get(provider.id);
      if (!existing || compareBestMatchEntries(candidate, existing) < 0) {
        byProvider.set(provider.id, candidate);
      }
    }
  });
  return [...byProvider.values()];
};

/** One winner per plan: score, primary specialty, specialty order, provider name. */
export const selectBestMatches = (pool: BestMatchEntry[]): BestMatchEntry[] =>
  [...pool].sort(compareBestMatchEntries).slice(0, 1);

// Realistic-reading sample providers so the design previews populated, not
// sparse — a session with few/no real matches shouldn't block reviewing the
// layout. Names are fixtures (ids are `__prototype-fixture-*`); never
// persisted or shown outside this dev-gated harness.
const FIXTURE_ROSTER: Array<Pick<NavigatorProvider, 'name' | 'location' | 'rating' | 'bio'>> = [
  { name: 'Dana Whitfield', location: 'Denver, CO', rating: 4.9, bio: 'Fifteen years coordinating multi-provider care plans for families managing long-distance caregiving.' },
  { name: 'Marcus Bellweather', location: 'Austin, TX', rating: 4.6, bio: 'Focuses on practical, budget-aware plans for families balancing work and caregiving.' },
  { name: 'Priya Anand', location: 'San Jose, CA', rating: 4.8, bio: 'Specializes in transitions between home care and assisted living, with a calm, detail-first approach.' },
  { name: 'Sofia Marchetti', location: 'Boston, MA', rating: 4.7, bio: 'Works closely with families navigating a recent diagnosis, translating options into next steps.' },
  { name: 'Elena Cho', location: 'Seattle, WA', rating: 4.9, bio: 'Known for responsive communication and a strong local provider network.' },
  { name: 'Theo Whitmore', location: 'Chicago, IL', rating: 4.5, bio: 'Brings a legal background to care planning conversations that touch benefits and estate concerns.' },
];

// Every category previews at least this many options — real providers are
// kept and counted first; fixtures only top up the remainder.
const MIN_PROVIDERS_PER_CATEGORY = 3;

const fixtureProviderId = (categoryId: string, index: number) => `__prototype-fixture-${categoryId}-${index}__`;

const populateCategory = (category: NavigatorCategory, rosterOffset: number): NavigatorCategory => {
  const needed = MIN_PROVIDERS_PER_CATEGORY - category.providers.length;
  if (needed <= 0) return category;
  const added: NavigatorProvider[] = Array.from({ length: needed }, (_, i) => {
    const fixture = FIXTURE_ROSTER[(rosterOffset + i) % FIXTURE_ROSTER.length];
    return {
      id: fixtureProviderId(category.id, i),
      specialtyName: category.name,
      rankOrder: category.providers.length + i + 1,
      ...fixture,
    };
  });
  return { ...category, providers: [...category.providers, ...added] };
};

/** Tops every category up to `MIN_PROVIDERS_PER_CATEGORY` with fixtures —
 * real providers are never replaced, only padded out. A session with zero
 * categories gets one fixture category so there's still somewhere to preview. */
const populateViewModel = (viewModel: NavigatorResultsViewModel): NavigatorResultsViewModel => {
  const categories =
    viewModel.categories.length > 0
      ? viewModel.categories
      : [{ id: '__prototype-fixture-category__', name: 'Sample specialty', isPrimary: true, providers: [], resources: [] }];
  return {
    ...viewModel,
    categories: categories.map((category, index) => populateCategory(category, index * MIN_PROVIDERS_PER_CATEGORY)),
  };
};

export const resolveBestMatches = (
  viewModel: NavigatorResultsViewModel,
  scenario: BestMatchScenario,
): BestMatchResult => {
  const populated = populateViewModel(viewModel);

  if (scenario === 'loading') return { status: 'loading', entries: [], providerIds: new Set(), viewModel: populated };
  if (scenario === 'error') return { status: 'error', entries: [], providerIds: new Set(), viewModel: populated };
  if (scenario === 'zero') return { status: 'ready', entries: [], providerIds: new Set(), viewModel: populated };

  const pool = poolAcrossCategories(populated);

  const entries = selectBestMatches(pool);
  return {
    status: 'ready',
    entries,
    providerIds: new Set(entries.map((entry) => entry.provider.id)),
    viewModel: populated,
  };
};
